import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis, } from "recharts";
import ChartApi from "../../../../../../core/api/chartApi";
import { homeIndexBarBaseRgbForCompositeValue, homeIndexBarMainRgbForCompositeValue, homeIndexBarRgbCss, } from "./homeIndexChartData";
const GRAD_PREFIX = "homeIdxBarGrad";
const MAX_RENDER_BARS = 125;
function yAxisDomainFromValues(values) {
    if (values.length === 0) {
        return [0, 100];
    }
    const minV = Math.min(...values);
    const maxV = Math.max(...values);
    const span = maxV - minV;
    if (span === 0) {
        const pad = Math.max(Math.abs(minV) * 0.08, 1);
        return [minV - pad, maxV + pad];
    }
    const cushion = span * 0.12;
    return [minV - cushion, maxV + cushion];
}
/**
 * Figma `1:4550` — discrete vertical bars with per-bar vertical gradient; colors by `compositeValue` band.
 */
const HomeIndexChart = () => {
    const [data, setData] = useState([]);
    useEffect(() => {
        let cancelled = false;
        ChartApi.getHomeIndexChart()
            .then((res) => {
            var _a;
            if (cancelled || !((_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.length)) {
                return;
            }
            const sorted = [...res.data].sort((a, b) => a.dateOfData.localeCompare(b.dateOfData));
            setData(sorted.map((row, idx) => ({
                i: idx,
                dateOfData: row.dateOfData,
                compositeValue: row.compositeValue,
                v: row.compositeValue,
            })));
        })
            .catch(() => {
            if (!cancelled) {
                setData([]);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const renderSubset = useMemo(() => {
        const sampledData = data.length <= MAX_RENDER_BARS
            ? data
            : (() => {
                const step = (data.length - 1) / (MAX_RENDER_BARS - 1);
                const sampledIndices = Array.from({ length: MAX_RENDER_BARS }, (_, idx) => Math.round(idx * step));
                const uniqueIndices = Array.from(new Set(sampledIndices)).sort((a, b) => a - b);
                return uniqueIndices.map((idx) => data[idx]).filter(Boolean);
            })();
        return sampledData.map((d, renderI) => (Object.assign(Object.assign({}, d), { renderI })));
    }, [data]);
    const xAxisTickValues = useMemo(() => renderSubset
        .map((d) => d.renderI)
        .filter((v) => Number.isFinite(v)), [renderSubset]);
    const xAxisDomain = useMemo(() => {
        if (xAxisTickValues.length === 0) {
            return [0, 0];
        }
        return [xAxisTickValues[0], xAxisTickValues[xAxisTickValues.length - 1]];
    }, [xAxisTickValues]);
    const yAxisDomain = useMemo(() => yAxisDomainFromValues(data.map((d) => d.v)), [data]);
    useEffect(() => {
        // Debug-only runtime visibility for manual validation of chart downsampling.
        console.log("[HomeIndexChart] render subset", {
            totalPoints: data.length,
            renderedPoints: renderSubset.length,
            subset: renderSubset.map((d) => ({
                renderI: d.renderI,
                i: d.i,
                dateOfData: d.dateOfData,
                v: d.v,
                compositeValue: d.compositeValue,
            })),
        });
    }, [data.length, renderSubset]);
    return (React.createElement(React.Fragment, null, data && data.length > 0 ? (React.createElement("div", { className: "home-index__chart-wrap", "data-node-id": "1:4550", "data-name": "Union" },
        React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
            React.createElement(BarChart, { data: renderSubset, margin: { top: 2, right: 2, bottom: 8, left: 2 }, barCategoryGap: "6%" },
                React.createElement("defs", null, renderSubset.map((d) => {
                    const top = homeIndexBarMainRgbForCompositeValue(d.compositeValue);
                    const base = homeIndexBarBaseRgbForCompositeValue(d.compositeValue);
                    return (React.createElement("linearGradient", { key: `${d.dateOfData}-${d.i}`, id: `${GRAD_PREFIX}-${d.i}`, x1: "0", y1: "1", x2: "0", y2: "0" },
                        React.createElement("stop", { offset: "0%", stopColor: homeIndexBarRgbCss(base), stopOpacity: 1 }),
                        React.createElement("stop", { offset: "100%", stopColor: homeIndexBarRgbCss(top), stopOpacity: 1 })));
                })),
                React.createElement(XAxis, { dataKey: "renderI", type: "number", domain: xAxisDomain, ticks: xAxisTickValues, hide: true }),
                React.createElement(YAxis, { domain: yAxisDomain, hide: true }),
                React.createElement(Bar, { dataKey: "v", isAnimationActive: false, maxBarSize: 8 }, renderSubset.map((d) => (React.createElement(Cell, { key: `${d.dateOfData}-${d.i}`, fill: `url(#${GRAD_PREFIX}-${d.i})` })))))))) : (React.createElement("div", { className: "home-index__chart-wrap home-index__chart-wrap--empty" },
        React.createElement("p", { style: { color: "white", textAlign: "center", width: "100%" } }, "No Data")))));
};
export default HomeIndexChart;
